import { Component, Input } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Interactive "lightness ladder" for the Lightness post, all sliders, no boxes. A hue
// slider and a lightness slider drive two swatches: the resulting color, and beside it
// the grey of that exact lightness. Drag the hue and the color races round the wheel
// while the grey holds still, because the grey is set by lightness alone, not hue, which
// is the post's whole point. Drag the lightness and both move together. Hue, saturation
// and the starting lightness come from the post's own color via [hex], so the widget
// opens on exactly that color. The post marks the mount point with
// <!--VAL-START-->...<!--VAL-END--> around a static fallback; the page swaps this live
// component in. Same design language as the value-rise widget.
@Component( {
  selector: 'app-value-ladder',
  templateUrl: './value-ladder.component.html',
  styleUrls: [ './value-ladder.component.scss' ],
} )
export class ValueLadderComponent {

  @Input() heading = 'Try It Yourself';

  // Hue, saturation and the starting lightness all come from the post color. Defaults to
  // the Lightness post's turquoise until a hex is bound.
  hue = 174;
  sat = 72;
  light = 56;
  private _hex = '#40E0D0';

  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( !rgb ) {
      return;
    }
    this._hex = '#' + String( value ).replace( /^#/, '' ).toUpperCase();
    const [ h, s, l ] = this.rgbToHsl( rgb );
    this.hue = h;
    this.sat = s;
    this.light = l;
  }
  get hex(): string {
    return this._hex;
  }

  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  // The color at the current hue/saturation/lightness.
  get colorHex(): string {
    return this.hslToHex( this.hue, this.sat, this.light );
  }

  // The pure grey of the current lightness (hue independent). Drag the hue and this
  // never changes, which is the lesson.
  get greyHex(): string {
    return this.hslToHex( 0, 0, this.light );
  }

  // Tints the slider thumbs with the live color so the controls read in context.
  get thumb(): string {
    return this.colorHex;
  }

  // Whole-number readouts (the values themselves stay precise).
  get lightLabel(): number {
    return Math.round( this.light );
  }
  get hueLabel(): number {
    return Math.round( this.hue );
  }

  onHue( value: string ): void {
    this.hue = this.clamp( parseInt( value, 10 ), 0, 359 );
    this.track();
  }

  onLight( value: string ): void {
    this.light = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'value-ladder' } );
  }

  private clamp( v: number, lo: number, hi: number ): number {
    if ( isNaN( v ) ) {
      return lo;
    }
    return Math.max( lo, Math.min( hi, v ) );
  }

  private parseHex( text: string ): [ number, number, number ] | null {
    const h = String( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  // [r,g,b] (0..255) to [hue 0..360, saturation 0..100, lightness 0..100], unrounded so
  // the post color reproduces exactly.
  private rgbToHsl( rgb: [ number, number, number ] ): [ number, number, number ] {
    const r = rgb[ 0 ] / 255, g = rgb[ 1 ] / 255, b = rgb[ 2 ] / 255;
    const mx = Math.max( r, g, b ), mn = Math.min( r, g, b ), d = mx - mn;
    const l = ( mx + mn ) / 2;
    let h = 0, s = 0;
    if ( d !== 0 ) {
      s = d / ( 1 - Math.abs( 2 * l - 1 ) );
      if ( mx === r ) {
        h = ( ( g - b ) / d ) % 6;
      } else if ( mx === g ) {
        h = ( b - r ) / d + 2;
      } else {
        h = ( r - g ) / d + 4;
      }
      h = ( h * 60 + 360 ) % 360;
    }
    return [ h, s * 100, l * 100 ];
  }

  private hslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;
    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if ( h < 60 ) { r = c; g = x; }
    else if ( h < 120 ) { r = x; g = c; }
    else if ( h < 180 ) { g = c; b = x; }
    else if ( h < 240 ) { g = x; b = c; }
    else if ( h < 300 ) { r = x; b = c; }
    else { r = c; b = x; }
    const to = ( v: number ) => Math.round( ( v + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
    return ( '#' + to( r ) + to( g ) + to( b ) ).toUpperCase();
  }
}
